/**
 * 称号显示工具（五处承载点共用）
 *
 * 设计约束，改这里之前先看：
 * 1. 这个游戏**没有稀有度色彩体系**，道具品质只是个数字。所以档位不靠新增色相区分，
 *    而是靠「字色深浅 + 有无底块 + 有无印章」的材质递进。
 *    蓝 #0080ff 是结构色、绿 #72b653 是成功色、红 #ff0000 是警示色——都不能拿来当称号档位。
 * 2. 明暗两套底色：列表/弹窗是白底，聊天区是深紫 #3d3250。
 *    同一个组件必须按 ground 取不同色值，否则聊天区里读不出来。
 * 3. 印章只编码「类别」，不表示强弱；档位只编码「稀有度」，不表示职业。
 */

/** 档位色值：light = 白底用，dark = 深紫底用 */
export const TIER_STYLES = {
  1: {
    // 几乎退到背景里：刚拿称号的新人
    light: { color: '#999999', fontWeight: 'normal', bg: 'transparent', border: 'transparent', sealColor: '#b0b0b0' },
    dark: { color: '#9d93ab', fontWeight: 'normal', bg: 'transparent', border: 'transparent', sealColor: '#8d8499' }
  },
  2: {
    // 与正文同强度，只是更小：常规职业
    light: { color: '#666666', fontWeight: 'normal', bg: 'transparent', border: 'transparent', sealColor: '#8c8c8c' },
    dark: { color: '#c9c0d6', fontWeight: 'normal', bg: 'transparent', border: 'transparent', sealColor: '#a89fb5' }
  },
  3: {
    // 明显高一档：开始占底块（复用全站蓝底白字标题条的语言）
    light: { color: '#333333', fontWeight: 'bold', bg: '#e8f2ff', border: '#0080ff', sealColor: '#0059b3' },
    dark: { color: '#ffffff', fontWeight: 'bold', bg: '#4a3f5c', border: '#7ab8ff', sealColor: '#7ab8ff' }
  },
  4: {
    // 唯一带贵气的一档：复用聊天区已有的暖金，不新造金色
    light: { color: '#8b6f47', fontWeight: 'bold', bg: '#fff8e6', border: '#e8cf9a', sealColor: '#8b6f47' },
    dark: { color: '#d4a574', fontWeight: 'bold', bg: '#4a3f5c', border: '#d4a574', sealColor: '#d4a574' }
  }
}

/**
 * 类别印章（1 个汉字）
 * 不用 emoji：与全站「bold + letter-spacing」的标题风格一致，跨端字形稳、缩放不糊，
 * 而且只占 1 个字宽——这是极窄空间里唯一可行的类别压缩手段。
 */
const SEAL_RULES = [
  [/铸造|铸/, '铸'],
  [/药剂|药/, '药'],
  [/木匠|木/, '匠'],
  [/花匠|花/, '花'],
  [/园艺|圃/, '圃'],
  [/厨师|厨/, '厨'],
  [/渔/, '渔'],
  [/农/, '农'],
  [/矿/, '矿'],
  [/脚夫|役/, '役'],
  [/混子|混/, '混']
]

/** 这些称号太泛，挂印章反而增加噪音 */
const NO_SEAL_TITLES = ['普通市民', '平民', '市民']

/**
 * 按称号名取类别印章；取不到返回空串（不渲染印章位）。
 * @param {String} title 称号全名
 */
export function sealOf (title) {
  if (!title) return ''
  for (let i = 0; i < NO_SEAL_TITLES.length; i++) {
    if (title.indexOf(NO_SEAL_TITLES[i]) > -1) return ''
  }
  for (let i = 0; i < SEAL_RULES.length; i++) {
    if (SEAL_RULES[i][0].test(title)) return SEAL_RULES[i][1]
  }
  return ''
}

/** 语义断点：从这些字符处截断，比硬切在半个词上好读得多 */
const BREAK_CHARS = ['·', '－', '-', '（', '(', '的']

/**
 * 称号截断：短名优先 → 语义断点 → 硬截加省略号。
 *
 * 纯 CSS 截断会把「王都维达尔加首席药剂师」切成「王都维达尔加首席…」，身份信息全丢，
 * 所以第一层必须是数据层的 short_title（运营手配 2-4 字）。
 *
 * @param {String} title     称号全名
 * @param {String} shortTitle 数据层短名，可为空
 * @param {Number} maxChars  该场景上限字数
 * @return {Object} { text, truncated } truncated=true 表示需要提供完整查看入口
 */
export function cutTitle (title, shortTitle, maxChars) {
  const full = title || ''
  const limit = maxChars > 0 ? maxChars : 99
  if (!full) return { text: '', truncated: false }

  // 第一层：数据层短名
  if (shortTitle && shortTitle.length <= limit) {
    return { text: shortTitle, truncated: full.length > shortTitle.length }
  }
  // 没超长就用全名
  if (full.length <= limit) {
    return { text: full, truncated: false }
  }
  // 第二层：语义断点（断点前的部分不超限才采信，否则会切出比硬截更短的无意义片段）
  for (let i = 0; i < BREAK_CHARS.length; i++) {
    const idx = full.indexOf(BREAK_CHARS[i])
    if (idx > 1 && idx <= limit) {
      return { text: full.substring(0, idx), truncated: true }
    }
  }
  // 第三层：硬截
  return { text: full.substring(0, limit - 1) + '…', truncated: true }
}

/**
 * 取档位样式，tier 非法或缺省时按 2 处理。
 * @param {Number} tier
 * @param {String} ground 'light' | 'dark'
 */
export function tierStyle (tier, ground) {
  const t = TIER_STYLES[tier] ? tier : 2
  return TIER_STYLES[t][ground === 'dark' ? 'dark' : 'light']
}

/**
 * 嵌在正文行里的称号（资料页 / 情报卡用）。
 *
 * 这几个位置周围是 14px 正文，称号如果还带底块和边框，就会变成「一个贴在文字里的按钮」——
 * 视觉上和输入框没区别。所以 plain 形态**不要底、不要框**，只靠字重和字色区分档位，
 * 颜色选深色系（这些位置是紫底 #9990c5 或米白纸底，浅色块一律糊）。
 */
export const TIER_PLAIN = {
  1: { color: '#6b6480', sealColor: '#7d7590' },
  2: { color: '#3a3350', sealColor: '#5a5170' },
  3: { color: '#003a7a', sealColor: '#003a7a' },
  4: { color: '#6b4c1e', sealColor: '#6b4c1e' }
}

/**
 * 取 plain 形态字色，tier 非法或缺省时按 2 处理。
 * @param {Number} tier
 */
export function plainTierStyle (tier) {
  return TIER_PLAIN[tier] || TIER_PLAIN[2]
}

/** 档位是否带底块（T3/T4）。用于「一屏底块不超过 4 条」的预算控制 */
export function hasBlock (tier) {
  return tier === 3 || tier === 4
}

export default { TIER_STYLES, TIER_PLAIN, sealOf, cutTitle, tierStyle, plainTierStyle, hasBlock }
